import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import {
  parseCSV,
  transformRowToUser,
  generateResultsCSV
} from '../../../utils/csvParser';
import {
  Loader2,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  X
} from 'lucide-react';

export const UserImportDialog = ({ open, onClose, onUsersImported }) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'validating' | 'importing' | 'complete'
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [validServiceDeskIds, setValidServiceDeskIds] = useState([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState({ created: [], updated: [], failed: [] });
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Reset state when dialog closes
  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setValidationWarnings([]);
    setValidServiceDeskIds([]);
    setImportProgress({ current: 0, total: 0 });
    setImportResults({ created: [], updated: [], failed: [] });
    setError(null);
    setIsDragOver(false);
    onClose();
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Le fichier doit être au format CSV');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setStep('validating');

    try {
      const rows = await parseCSV(selectedFile);
      const users = rows.map(transformRowToUser);
      setParsedData(rows);

      // Validate on backend
      const response = await fetchWithAuth('/users/validate-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users })
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Erreur lors de la validation');
      }

      if (json.success) {
        setValidationErrors(json.data.errors || []);
        setValidationWarnings(json.data.warnings || []);
        setValidServiceDeskIds(json.data.validServiceDeskIds || []);
        setStep('preview');
      } else {
        throw new Error(json.error || 'Erreur lors de la validation');
      }
    } catch (err) {
      setError(err.message);
      setStep('upload');
    }
  }, []);

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files?.[0];
    handleFileSelect(selectedFile);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Handle import
  const handleImport = async () => {
    setStep('importing');
    setError(null);

    try {
      // Transform rows to user objects
      const users = parsedData.map(transformRowToUser);
      setImportProgress({ current: 0, total: users.length });

      const response = await fetchWithAuth('/users/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users })
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Erreur lors de l\'import');
      }

      if (json.success) {
        setImportResults(json.data);
        setImportProgress({ current: users.length, total: users.length });
        setStep('complete');
        onUsersImported();
      } else {
        throw new Error(json.error || 'Erreur lors de l\'import');
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.message);
      setStep('preview');
    }
  };

  // Download results CSV
  const handleDownloadResults = () => {
    const csvContent = generateResultsCSV(importResults);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-users-results-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Reset to upload step
  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setValidationWarnings([]);
    setValidServiceDeskIds([]);
    setError(null);
  };

  const handleDownloadTemplate = () => {
    const templateContent = `Email,Nom,Role (user/technician/manager/admin/superadmin),Service Desk ID (ex: 3 ou 205 ou *),Mode (r = lecture / rw = lecture-ecriture),Secteurs (ex: L18 ou * pour tous),Service Desks Autorises - techniciens uniquement (ex: 3;205;238)
john.doe@example.com,John Doe,technician,3;205,rw,L18;L19,3;205`;

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + templateContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderUploadStep = () => (
    <>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-lg font-medium text-gray-700 mb-2">
          Glissez-déposez votre fichier CSV ici
        </p>
        <p className="text-sm text-gray-500 mb-4">ou</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          Parcourir les fichiers
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileInputChange}
        />
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">Format CSV attendu</h4>
        <p className="text-sm text-gray-600 mb-2">
          Colonnes requises : <code className="bg-gray-200 px-1 rounded">Email</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Nom</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Role</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Service Desk ID</code>
        </p>
        <p className="text-sm text-gray-600 mb-2">
          Colonnes optionnelles : <code className="bg-gray-200 px-1 rounded">Mode</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Secteurs</code>,{' '}
          <code className="bg-gray-200 px-1 rounded">Service Desks Autorisés</code>{' '}
          <span className="text-gray-400">(techniciens uniquement)</span>
        </p>
        <p className="text-sm text-gray-500">
          Les mots de passe seront générés automatiquement.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Rôles valides : user, technician, manager, admin, superadmin
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={handleDownloadTemplate}
        >
          <Download className="w-4 h-4 mr-2" />
          Télécharger le modèle CSV
        </Button>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <XCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
    </>
  );

  const renderPreviewStep = () => {
    const hasErrors = validationErrors.length > 0;
    const previewRows = parsedData.slice(0, 5);

    return (
      <>
        {file && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg mb-4">
            <FileText className="w-5 h-5 text-gray-500" />
            <span className="font-medium">{file.name}</span>
            <span className="text-sm text-gray-500">({parsedData.length} utilisateurs)</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={handleReset}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {hasErrors && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
              <XCircle className="w-5 h-5" />
              Erreurs de validation ({validationErrors.length})
            </div>
            <ul className="text-sm text-red-600 space-y-1 max-h-32 overflow-y-auto">
              {validationErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {validationWarnings.length > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-700 font-medium mb-2">
              <AlertTriangle className="w-5 h-5" />
              Avertissements ({validationWarnings.length})
            </div>
            <ul className="text-sm text-yellow-600 space-y-1 max-h-24 overflow-y-auto">
              {validationWarnings.map((warn, i) => (
                <li key={i}>{warn}</li>
              ))}
            </ul>
          </div>
        )}

        {!hasErrors && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700 font-medium">
              <CheckCircle className="w-5 h-5" />
              Validation réussie - {parsedData.length} utilisateur(s) prêt(s) à être importé(s)
            </div>
          </div>
        )}

        <div className="mb-4">
          <h4 className="font-medium text-gray-700 mb-2">
            Aperçu (5 premières lignes)
          </h4>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Username</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Role</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Service Desk</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2">{row.email}</td>
                    <td className="px-3 py-2">{row.username}</td>
                    <td className="px-3 py-2">{row.role}</td>
                    <td className="px-3 py-2">{row.servicedeskid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {parsedData.length > 5 && (
            <p className="text-sm text-gray-500 mt-2">
              ... et {parsedData.length - 5} autre(s) utilisateur(s)
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
            <XCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={hasErrors}
          >
            Importer {parsedData.length} utilisateur(s)
          </Button>
        </DialogFooter>
      </>
    );
  };

  const renderValidatingStep = () => (
    <div className="py-8 text-center">
      <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
      <p className="text-lg font-medium text-gray-700 mb-2">
        Validation en cours...
      </p>
      <p className="text-sm text-gray-500">
        Vérification des données du fichier CSV
      </p>
    </div>
  );

  const renderImportingStep = () => (
    <div className="py-8 text-center">
      <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
      <p className="text-lg font-medium text-gray-700 mb-2">
        Import en cours...
      </p>
      <p className="text-sm text-gray-500">
        {importProgress.current} / {importProgress.total} utilisateurs traités
      </p>
      <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5 max-w-md mx-auto">
        <div
          className="bg-primary h-2.5 rounded-full transition-all duration-300"
          style={{
            width: importProgress.total > 0
              ? `${(importProgress.current / importProgress.total) * 100}%`
              : '0%'
          }}
        />
      </div>
    </div>
  );

  const renderCompleteStep = () => {
    const { created, updated, failed } = importResults;
    const hasGeneratedPasswords = created.some(item => item.generatedPassword);

    return (
      <>
        <div className="py-4 text-center">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-4">
            Import terminé
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <p className="text-2xl font-bold text-green-700">{created.length}</p>
            <p className="text-sm text-green-600">Créé(s)</p>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <p className="text-2xl font-bold text-blue-700">{updated.length}</p>
            <p className="text-sm text-blue-600">Mis à jour</p>
          </div>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
            <p className="text-2xl font-bold text-red-700">{failed.length}</p>
            <p className="text-sm text-red-600">Échec(s)</p>
          </div>
        </div>

        {failed.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="font-medium text-red-700 mb-2">Erreurs :</p>
            <ul className="text-sm text-red-600 space-y-1 max-h-24 overflow-y-auto">
              {failed.map((item, i) => (
                <li key={i}>{item.email}: {item.error}</li>
              ))}
            </ul>
          </div>
        )}

        {hasGeneratedPasswords && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-700 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Mots de passe générés</span>
            </div>
            <p className="text-sm text-yellow-600">
              Des mots de passe ont été générés pour les nouveaux utilisateurs.
              Téléchargez le rapport pour les récupérer.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadResults}
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger le rapport
          </Button>
          <Button type="button" onClick={handleClose}>
            Fermer
          </Button>
        </DialogFooter>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Importer des utilisateurs</DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Importez une liste d\'utilisateurs à partir d\'un fichier CSV'}
            {step === 'validating' && 'Validation des données en cours...'}
            {step === 'preview' && 'Vérifiez les données avant l\'import'}
            {step === 'importing' && 'Import en cours...'}
            {step === 'complete' && 'Import terminé'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {step === 'upload' && renderUploadStep()}
          {step === 'validating' && renderValidatingStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'importing' && renderImportingStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>

        {step === 'upload' && (
          <DialogFooter className="flex-shrink-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
